import { useState } from 'react'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import DeleteIcon from 'components/material-symbol/icons/Delete'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import { formatNameForDisplay } from 'lib/text-display'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { useMutation } from '@apollo/client'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import DisplayContact from 'pages/pga-coach/students/student-show-page/details-components/display-contact'
import GolferInformation from 'pages/pga-coach/students/student-show-page/details-components/golfer-information'
import {
  genderOptions,
  pronounOptions,
} from 'pages/pga-coach/students/student-show-page/details-components/constants'
import {
  ContactField,
  DetailField,
  DetailSection,
} from 'pages/pga-coach/students/student-show-page/details-components/content-box'
import HeightOutlinedIcon from 'components/material-symbol/icons/Height'
import PeopleOutlineIcon from 'components/material-symbol/icons/PeopleOutline'
import RecordVoiceOverOutlinedIcon from 'components/material-symbol/icons/ThreeP'
import AccessibleForwardOutlinedIcon from 'components/material-symbol/icons/AccessibleForward'
import { makeStyles } from '@material-ui/styles'
import DeleteGuardianDialog from './delete-guardian-dialog'
import CompleteGolferProfileCta from './complete-golfer-profile-cta'
import { DELETE_REPRESENTATIVE } from './queries'

const useParticipantStyles = makeStyles(theme => ({
  contentStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    paddingTop: theme.spacing(2.5),
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
}))

const ParticipantInformation = ({ participant, refetch, profileEditUrl }) => {
  const participantClasses = useParticipantStyles()
  const {
    firstName,
    lastName,
    isMinor,
    contact,
    medicalProfile,
    representatives,
    golferProfile,
  } = participant
  const studentName = formatNameForDisplay(`${firstName} ${lastName}`)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [guardianToDelete, setGuardianToDelete] = useState(null)

  const [deleteStudentGuardian, { loading }] = useMutation(
    DELETE_REPRESENTATIVE,
  )

  const handleDeleteClick = representative => {
    setGuardianToDelete(representative)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!guardianToDelete) return

    try {
      const { data } = await deleteStudentGuardian({
        variables: {
          representativeId: guardianToDelete.id,
        },
      })
      if (data?.deleteRepresentative.success) {
        snackbarSuccess('Guardian was successfully deleted')
        setDeleteModalOpen(false)
        setGuardianToDelete(null)
        refetch()
      } else {
        snackbarError(
          `Error deleting guardian: ${data?.deleteRepresentative.message}`,
        )
      }
    } catch (error) {
      snackbarError(`Error deleting guardian: ${error.message}`)
    }
  }

  const otherGuardians = representatives.filter(
    r => r.relationshipType !== 'SELF' && r.contact?.id !== contact?.id,
  )

  const primaryRepresentative = representatives.find(
    r => r.contact?.id === contact?.id,
  )

  const contactForDisplay = contact
    ? {
      ...contact,
      phoneNumber: contact.phone,
      relationshipType: primaryRepresentative?.relationshipType,
    }
    : null

  const demographics = {
    studentGender: golferProfile?.gender || '',
    studentGenderSelfDescription: golferProfile?.genderSelfDescription || '',
    studentHeightInches: golferProfile?.heightInInches || '',
    studentPhysicalLimitations: golferProfile?.physicalLimitations || '',
    studentProunounPreference: golferProfile?.pronouns || '',
  }

  const heightInFeet = Math.floor(demographics.studentHeightInches / 12)
  const heightInInches = demographics.studentHeightInches % 12
  const heightText = demographics.studentHeightInches
    ? `${heightInFeet}' ${heightInInches}"`
    : null

  const genderValue = genderOptions.find(
    opt => opt.value === demographics.studentGender,
  )?.label
  const genderText = demographics.studentGenderSelfDescription
    ? `${genderValue}: (${demographics.studentGenderSelfDescription})`
    : genderValue

  const pronounValue = pronounOptions.find(
    opt => opt.value === demographics.studentProunounPreference,
  )?.label

  const hasDemographics =
    heightText ||
    genderText ||
    pronounValue ||
    demographics.studentPhysicalLimitations

  return (
    <Container maxWidth={false} style={{ maxWidth: 832, padding: '0 16px' }}>
      <Box className={participantClasses.contentStack}>
        <CompleteGolferProfileCta
          student={participant}
          profileEditUrl={profileEditUrl}
        />
        <DetailSection title="Student" compact layout="plain">
          <Box className={participantClasses.fieldsGrid}>
            <ContactField
              icon={PersonOutlineIcon}
              label="Name"
              value={studentName}
              testId="participant-student-name"
            />
            <DisplayContact
              isMinor={isMinor}
              contact={contactForDisplay}
              embedded
            />
          </Box>
        </DetailSection>

        {hasDemographics && (
          <DetailSection title="About" compact layout="plain">
            <Box className={participantClasses.fieldsGrid}>
              {heightText && (
                <ContactField
                  icon={HeightOutlinedIcon}
                  label="Height"
                  value={heightText}
                />
              )}
              {genderText && (
                <ContactField
                  icon={PeopleOutlineIcon}
                  label="Gender"
                  value={genderText}
                />
              )}
              {pronounValue && (
                <ContactField
                  icon={RecordVoiceOverOutlinedIcon}
                  label="Pronouns"
                  value={pronounValue}
                />
              )}
              {demographics.studentPhysicalLimitations && (
                <ContactField
                  icon={AccessibleForwardOutlinedIcon}
                  label="Physical limitations"
                  value={demographics.studentPhysicalLimitations}
                />
              )}
            </Box>
          </DetailSection>
        )}

        <GolferInformation golferProfile={golferProfile} compact />

        {medicalProfile?.info && (
          <DetailSection title="Allergies and medications" compact stacked>
            <DetailField
              icon={InfoOutlinedIcon}
              label="Details"
              value={medicalProfile.info}
              testId="participant-medical-info"
            />
          </DetailSection>
        )}

        {isMinor && otherGuardians.length > 0 && (
          <DetailSection title="Other guardians" compact stacked>
            {otherGuardians.map(representative => {
              const guardianContact = representative.contact
              const guardianLabel = `${guardianContact.firstName} ${guardianContact.lastName}`
              const guardianDetail = guardianContact.email
                ? `${guardianContact.email}${
                  guardianContact.phone
                    ? ` · ${formatPhoneNumber(guardianContact.phone)}`
                    : ''
                }`
                : formatPhoneNumber(guardianContact.phone) || 'N/A'

              return (
                <Box
                  key={representative.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={16}
                >
                  <Box flex="1 1 auto" minWidth={0}>
                    <DetailField
                      icon={PersonOutlineIcon}
                      label={guardianLabel}
                      value={guardianDetail}
                    />
                  </Box>
                  <Tooltip title="Delete guardian">
                    <IconButton
                      onClick={() => handleDeleteClick(representative)}
                      aria-label="Delete guardian"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            })}
          </DetailSection>
        )}
      </Box>

      <DeleteGuardianDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        guardian={guardianToDelete}
        onDelete={handleConfirmDelete}
        loading={loading}
      />
    </Container>
  )
}

export default ParticipantInformation
